import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, MessageCircle, Bot, TrendingUp, RefreshCw, ExternalLink } from 'lucide-react';
import KitePortfolio from '@/components/portfolio/KitePortfolio';
import PortfolioChatComponent from '@/components/chat/PortfolioChatComponent';
import { Badge } from '@/components/ui/badge';
import { isAuthenticated, initializeAuthState } from '@/utils/kiteConnect';
import KiteConnectAuth from '@/components/auth/KiteConnectAuth';

const PortfolioWithAI: React.FC = () => {
  const [activeTab, setActiveTab] = useState("portfolio");
  const [isConnected, setIsConnected] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);

  // Check connection status on component mount
  useEffect(() => {
    initializeAuthState();
    setIsConnected(isAuthenticated());
  }, []);

  // Handle auth success
  const handleAuthSuccess = (userData: any) => {
    setIsConnected(true);
    setShowConnectDialog(false);
  };

  // Handle auth error
  const handleAuthError = (error: string) => {
    console.error('Auth error:', error);
  };

  // Handle auth clear
  const handleAuthClear = () => {
    setIsConnected(false);
  };

  const features = [
    {
      icon: BarChart3,
      title: "Visual Portfolio",
      description: "View your holdings, positions, and performance metrics in beautiful charts and tables",
      tab: "portfolio"
    },
    {
      icon: Bot,
      title: "AI Portfolio Analysis", 
      description: "Get personalized insights, risk assessments, and investment recommendations from AI",
      tab: "ai-chat"
    },
    {
      icon: TrendingUp,
      title: "Smart Insights",
      description: "AI-powered analysis of your actual portfolio data with actionable recommendations",
      tab: "ai-chat"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-gradient-to-r from-[#9E7C47]/10 to-[#9E7C47]/5 border-[#9E7C47]/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-[#9E7C47]" />
                Portfolio Dashboard
                {isConnected && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    Connected
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-2">
                {isConnected 
                  ? "Manage your portfolio and get AI-powered insights about your investments"
                  : "Connect your Zerodha account to unlock AI-powered portfolio analysis"
                }
              </CardDescription>
            </div>
            {!isConnected && (
              <Button 
                className="bg-[#9E7C47] hover:bg-[#9E7C47]/90"
                onClick={() => setShowConnectDialog(true)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Connect Zerodha
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Features Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card 
              key={feature.title}
              className="cursor-pointer hover:bg-[#9E7C47]/5 transition-colors border-[#9E7C47]/20"
              onClick={() => setActiveTab(feature.tab)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 text-[#9E7C47] mt-1" />
                  <div>
                    <h3 className="font-medium text-sm">{feature.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Dashboard */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 bg-black/50 border-[#9E7C47]/30">
          <TabsTrigger 
            value="portfolio" 
            className="data-[state=active]:bg-[#9E7C47] data-[state=active]:text-white"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Portfolio View
          </TabsTrigger>
          <TabsTrigger 
            value="ai-chat"
            className="data-[state=active]:bg-[#9E7C47] data-[state=active]:text-white"
          >
            <Bot className="h-4 w-4 mr-2" />
            AI Assistant
            {isConnected && (
              <Badge className="ml-2 bg-green-500/20 text-green-400 text-xs">
                Enhanced
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="portfolio" className="space-y-4">
          <KitePortfolio />
          
          {isConnected && (
            <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bot className="h-5 w-5 text-blue-400" />
                    <div>
                      <h3 className="font-medium text-sm">Want AI insights about your portfolio?</h3>
                      <p className="text-xs text-muted-foreground">
                        Switch to the AI Assistant tab for personalized analysis and recommendations
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setActiveTab("ai-chat")}
                    className="border-blue-500/30 hover:bg-blue-500/10"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    Ask AI
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="ai-chat" className="space-y-4">
          <PortfolioChatComponent />
          
          {isConnected && (
            <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <div>
                    <h3 className="font-medium text-sm">AI Enhanced with Your Portfolio Data</h3>
                    <p className="text-xs text-muted-foreground">
                      The AI has access to your actual Zerodha holdings and can provide personalized insights, 
                      risk analysis, and investment recommendations based on your real portfolio.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Zerodha Connection Dialog */}
      {showConnectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-[#9E7C47]/30 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-[#9E7C47]/20">
              <h2 className="text-lg font-semibold text-[#9E7C47]">Connect to Zerodha</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConnectDialog(false)}
                className="text-gray-400 hover:text-white"
              >
                ×
              </Button>
            </div>
            <div className="p-4">
              <KiteConnectAuth
                onAuthSuccess={handleAuthSuccess}
                onAuthError={handleAuthError}
                onAuthClear={handleAuthClear}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioWithAI;